import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MEGA_MENU_GROUPS, hasPermission } from '../../config/navigation.config';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';

interface AllItemsMegaMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectModule?: (route: string, label: string) => void;
}

const AllItemsMegaMenu: React.FC<AllItemsMegaMenuProps> = ({ isOpen, onClose, onSelectModule }) => {
  const projectId = useCurrentProjectId();
  const isDisabled = !projectId;
  const menuRef = useRef<HTMLDivElement>(null);

  // Mock user permissions - in real app, get from auth context
  const userPermissions = [
    'purchase_orders.view',
    'bills.view',
    'change_orders.view',
    'client_invoices.view',
    'daily_logs.view',
    'timesheets.view',
    'todos.view',
    'rfis.view',
    'files.view',
    'calendar.view',
  ];

  // Handle ESC key to close menu
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleArrowNavigation = (event: KeyboardEvent) => {
      if (!menuRef.current) return;

      const items = menuRef.current.querySelectorAll('[data-menu-item]');
      const currentIndex = Array.from(items).findIndex(item => item === document.activeElement);

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
          (items[nextIndex] as HTMLElement)?.focus();
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
          (items[prevIndex] as HTMLElement)?.focus();
          break;
        }
        case 'Enter':
          if (document.activeElement && (document.activeElement as HTMLElement).dataset.menuItem !== undefined) {
            (document.activeElement as HTMLElement).click();
          }
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('keydown', handleArrowNavigation);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleArrowNavigation);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="absolute top-full left-0 z-50 bg-white border border-gray-200 shadow-xl rounded-lg mt-2 min-w-[600px] max-w-4xl"
      role="menu"
      aria-label="All project modules"
    >
      <div className="p-6">
        <div className="grid grid-cols-2 gap-8">
          {MEGA_MENU_GROUPS.map(group => (
            <div key={group.key}>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-100">
                {group.label}
              </h3>
              <ul className="space-y-1" role="group" aria-labelledby={`${group.key}-heading`}>
                {group.items
                  .filter(item => hasPermission(item, userPermissions))
                  .map(item => {
                    const Icon = item.icon;
                    const isOrgLevel = item.level === 'org';
                    const href = isOrgLevel
                      ? item.route('global')
                      : item.route('project', projectId || undefined);

                    return (
                      <li key={item.key} role="none">
                        {isOrgLevel || !isDisabled ? (
                          <Link
                            to={href}
                            onClick={onClose}
                            className="flex items-center text-sm text-gray-700 hover:text-brand-primary hover:bg-red-50 px-3 py-2 rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2"
                            role="menuitem"
                            data-menu-item
                            tabIndex={0}
                          >
                            <Icon
                              className="h-4 w-4 mr-3 flex-shrink-0 text-gray-500"
                              aria-hidden="true"
                            />
                            <span className="text-sm font-medium">{item.label}</span>
                          </Link>
                        ) : (
                          <button
                            onClick={() => {
                              const routePattern = item.route('project', ':projectId');
                              onClose();
                              onSelectModule?.(routePattern, item.label);
                            }}
                            className="flex items-center text-sm text-gray-700 hover:text-brand-primary hover:bg-red-50 px-3 py-2 rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 w-full text-left"
                            role="menuitem"
                            data-menu-item
                            tabIndex={0}
                          >
                            <Icon
                              className="h-4 w-4 mr-3 flex-shrink-0 text-gray-500"
                              aria-hidden="true"
                            />
                            <span className="text-sm font-medium">{item.label}</span>
                          </button>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Info banner when no project is selected */}
      {isDisabled && (
        <div className="bg-blue-50 border-t border-blue-200 px-6 py-4 rounded-b-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-600 mt-0.5"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-800">No Project Selected</p>
              <p className="text-xs text-blue-700 mt-1">
                Click any module to select a project first.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllItemsMegaMenu;
